import React, { useEffect, useState } from "react";
import { Search, Phone, Mail, MessageSquare, RefreshCw, MapPin, FileText, Share, ExternalLink, ChevronRight } from "lucide-react";
import { urlServeur } from "../espace/api";
import { api } from "../console/api";
import { RondFiche } from "../console/PhotoEleve";
import { Groupe, TitreGroupe, Retour, Vide, Badge } from "../espace/ui";
import { dateCourte, francs, tel } from "./outils";

const actif = (i) => !/^(annul|désinscrit|desinscrit|liste)/i.test(String(i.statut || ""));
const age = (n) => {
  if (!n) return "";
  const d = new Date(n); const a = new Date();
  let x = a.getFullYear() - d.getFullYear();
  if (a < new Date(a.getFullYear(), d.getMonth(), d.getDate())) x -= 1;
  return Number.isFinite(x) ? `${x} ans` : "";
};

/* Une facture : on la touche, le PDF se prépare ; « Ouvrir » l'affiche, « Partager » ouvre
   la feuille de partage du téléphone (WhatsApp, e-mail, Messages…) avec le PDF joint. */
const LigneFacture = ({ x, onChange }) => {
  const [ouvert, setOuvert] = useState(false);
  const [fichier, setFichier] = useState(null);
  const [etat, setEtat] = useState("");
  const [cash, setCash] = useState("");
  const url = x.lien ? urlServeur(x.lien) : "";
  // « La maman m'a donné 450.– au cours » : enregistré tout de suite, plus de rappel.
  const encaisser = async () => {
    const montant = Number(String(cash).replace(",", "."));
    if (!montant || !window.confirm(`Enregistrer CHF ${montant.toFixed(2)} reçus en espèces pour la facture ${x.numero} ?`)) return;
    try {
      const r = await api(`/mobile/facture/${encodeURIComponent(x.numero)}/especes`, { method: "POST", body: JSON.stringify({ montant }) });
      setEtat(r.reste > 0.5 ? `Encaissé. Il reste CHF ${r.reste.toFixed(2)}.` : "Encaissé : la facture est payée.");
      setCash(""); onChange?.();
    } catch (e) { setEtat(e.message); }
  };
  const du = x.solde > 0.5;
  // Le PDF est téléchargé dès l'ouverture de la ligne : le partage doit partir au tap
  // même (l'iPhone refuse un partage lancé après une attente).
  const ouvrir = async () => {
    setOuvert(!ouvert);
    if (!ouvert && url && !fichier) {
      try {
        const r = await fetch(url);
        if (!r.ok) throw new Error();
        setFichier(new File([await r.blob()], `Facture-${x.numero}.pdf`, { type: "application/pdf" }));
      } catch { setEtat("PDF indisponible pour l'instant."); }
    }
  };
  const partager = async () => {
    const texte = `Facture ${x.numero} — 1000 ARTS${du ? ` · reste à payer CHF ${x.solde.toFixed(2)}` : ""}`;
    try {
      if (fichier && navigator.canShare?.({ files: [fichier] })) {
        await navigator.share({ files: [fichier], title: `Facture ${x.numero}`, text: texte });
      } else if (navigator.share) {
        await navigator.share({ title: `Facture ${x.numero}`, text: texte, url });
      } else {
        window.open(`https://wa.me/?text=${encodeURIComponent(`${texte}\n${url}`)}`, "_blank");
      }
    } catch { /* partage annulé */ }
  };
  return (
    <div data-testid={`ecole-facture-${x.numero}`}>
      <button onClick={ouvrir} className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-[#F1F5F9]">
        <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
          style={{ background: du ? "#FEF3C7" : "#DCFCE7", color: du ? "#B45309" : "#15803D" }}><FileText size={18} /></span>
        <span className="flex-1 min-w-0">
          <span className="block text-[14.5px] truncate">{x.semestre || x.numero}</span>
          <span className="block text-[12px] text-[var(--muted)] truncate">{x.numero}{x.envoyee_le ? ` · envoyée le ${dateCourte(x.envoyee_le)}` : " · pas encore envoyée"}</span>
        </span>
        <span className="text-right shrink-0">
          <span className="block text-[14.5px] font-semibold tabular-nums">{francs(x.total)}</span>
          {du ? <Badge couleur="#F59E0B">reste {francs(x.solde)}</Badge> : <Badge couleur="#16A34A">payée</Badge>}
        </span>
        <ChevronRight size={16} style={{ color: "#C4C9D0", transform: ouvert ? "rotate(90deg)" : undefined }} />
      </button>
      {ouvert && (
        <div className="px-4 pb-3 flex gap-2">
          {url ? (
            <>
              <a href={url} target="_blank" rel="noopener noreferrer" className="btn-ghost text-sm flex-1 justify-center"
                data-testid="ecole-facture-ouvrir"><ExternalLink size={14} /> Ouvrir</a>
              <button onClick={partager} disabled={!fichier && !navigator.share} className="btn-primary text-sm flex-1 justify-center"
                data-testid="ecole-facture-partager"><Share size={14} /> {fichier ? "Partager" : "Préparation…"}</button>
            </>
          ) : <p className="text-[13px] text-[var(--muted)]">Pas encore de PDF pour cette facture.</p>}
        </div>
      )}
      {ouvert && du && (
        <div className="px-4 pb-3 flex gap-2 items-center" data-testid="ecole-facture-especes">
          <input inputMode="decimal" className="input-field flex-1" placeholder={`Espèces reçues (reste ${x.solde.toFixed(2)})`}
            value={cash} onChange={(e) => setCash(e.target.value)} />
          <button className="btn-ghost text-sm shrink-0" onClick={() => setCash(x.solde.toFixed(2))}>Tout</button>
          <button className="btn-primary text-sm shrink-0" disabled={!cash} onClick={encaisser} data-testid="ecole-facture-encaisser">Encaisser</button>
        </div>
      )}
      {etat && <p className="px-4 pb-3 text-[12.5px] text-[var(--muted)]">{etat}</p>}
    </div>
  );
};

/* La fiche d'un élève : qui il est, ses parents à un tap, ses cours, ce que la famille doit. */
const Fiche = ({ id, onRetour, onOuvrir }) => {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [erreur, setErreur] = useState("");
  useEffect(() => {
    setD(null); setF(null);
    api(`/eleves/${encodeURIComponent(id)}`).then(setD).catch((e) => setErreur(e.message));
    api(`/mobile/famille/${encodeURIComponent(id)}/factures`).then(setF).catch(() => setF({ factures: [] }));
  }, [id]);
  if (erreur) return <><Retour onClick={onRetour} /><Vide>{erreur}</Vide></>;
  if (!d) return <div className="py-16 grid place-items-center"><RefreshCw className="animate-spin" size={20} /></div>;
  const { eleve: e, famille: fam = {} } = d;
  const cours = (d.inscriptions || []).filter(actif);
  const anciens = (d.inscriptions || []).filter((i) => !actif(i));
  return (
    <div className="space-y-5" data-testid="ecole-fiche-eleve">
      <Retour onClick={onRetour} label="Élèves" />
      <div className="flex items-center gap-3">
        {/* La photo choisie par la famille dans son app (la photo seulement), sinon l'initiale. */}
        <RondFiche eleve={e} taille={56} avecAvatar={false} testId="ecole-fiche-photo" sinon={
          <div className="w-14 h-14 rounded-full grid place-items-center text-white text-xl font-semibold shrink-0"
            style={{ background: "linear-gradient(140deg,#00C6CE,#0F1B2D)" }}>{(e.prenom || "?")[0]}</div>} />
        <div className="min-w-0">
          <div className="font-display text-[24px] leading-tight">{e.prenom} {e.nom}</div>
          <div className="text-[13px] text-[var(--muted)]">
            {[age(e.date_naissance), e.statut_membre].filter(Boolean).join(" · ")}
          </div>
        </div>
      </div>

      <section>
        <TitreGroupe>Famille</TitreGroupe>
        <Groupe>
          <div className="px-4 py-3">
            <div className="text-[15px] font-semibold">{fam.prenom_responsable} {fam.nom_responsable}</div>
            <div className="text-[12.5px] text-[var(--muted)] mt-0.5 flex items-start gap-1">
              <MapPin size={12} className="mt-0.5 shrink-0" />
              {[fam.adresse, [fam.npa, fam.localite].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "Adresse manquante"}
            </div>
          </div>
          <div className="grid grid-cols-3 divide-x" style={{ borderColor: "var(--border)" }}>
            {[["Appeler", Phone, fam.telephone && tel(fam.telephone)],
              ["SMS", MessageSquare, fam.telephone && `sms:${String(fam.telephone).replace(/[^\d+]/g, "")}`],
              ["E-mail", Mail, fam.email && `mailto:${fam.email}`]].map(([l, I, href]) => (
              <a key={l} href={href || undefined} aria-disabled={!href}
                className="flex flex-col items-center gap-1 py-3 text-[12px] font-medium"
                style={{ color: href ? "var(--primary)" : "#CBD5E1" }}>
                <I size={18} /> {l}
              </a>
            ))}
          </div>
          <div className="px-4 py-3 text-[13px] text-[var(--muted)]">
            {fam.telephone || "pas de téléphone"} · {fam.email || "pas d'e-mail"}
          </div>
        </Groupe>
      </section>

      <section>
        <TitreGroupe>Cours</TitreGroupe>
        <Groupe>
          {cours.length ? cours.map((i) => (
            <div key={i.id} className="px-4 py-3">
              <div className="text-[15px]">{i.cours_nom}</div>
              <div className="text-[12.5px] text-[var(--muted)]">{i.jour} {i.heure} · {i.statut}{i.formule ? ` · ${i.formule}` : ""}</div>
            </div>
          )) : <p className="px-4 py-3 text-sm text-[var(--muted)]">Aucun cours actif.</p>}
          {anciens.slice(0, 5).map((i) => (
            <div key={i.id} className="px-4 py-3 opacity-60">
              <div className="text-[14px]">{i.cours_nom}</div>
              <div className="text-[12px] text-[var(--muted)]">{i.statut}{i.desinscription_le ? ` le ${dateCourte(i.desinscription_le)}` : ""}</div>
            </div>
          ))}
          {(d.essais || []).map((x) => (
            <div key={x.id} className="px-4 py-3">
              <div className="text-[14px]">Essai : {x.cours_nom}</div>
              <div className="text-[12px] text-[var(--muted)]">{dateCourte(x.date_essai)} · {x.statut}</div>
            </div>
          ))}
        </Groupe>
      </section>

      <section>
        <TitreGroupe>Identité</TitreGroupe>
        <Groupe>
          {[["Naissance", dateCourte(e.date_naissance)], ["N° AVS", e.avs || "manquant"],
            ["Photos", (cours[0]?.droit_image) || "—"]].map(([l, v]) => (
            <div key={l} className="flex justify-between gap-3 px-4 py-3 text-[14.5px]">
              <span className="text-[var(--muted)]">{l}</span>
              <span style={v === "manquant" ? { color: "#DC2626" } : undefined}>{v}</span>
            </div>
          ))}
        </Groupe>
      </section>

      <section>
        <TitreGroupe>Factures de la famille</TitreGroupe>
        <Groupe>
          {!f ? <p className="px-4 py-3 text-sm text-[var(--muted)]">Chargement…</p>
            : !f.factures.length ? <p className="px-4 py-3 text-sm text-[var(--muted)]">Aucune facture.</p>
              : f.factures.map((x) => <LigneFacture key={x.numero} x={x}
                onChange={() => api(`/mobile/famille/${encodeURIComponent(id)}/factures`).then(setF).catch(() => {})} />)}
        </Groupe>
      </section>

      {(d.fratrie || []).length > 0 && (
        <section>
          <TitreGroupe>Frères et sœurs</TitreGroupe>
          <Groupe>
            {d.fratrie.map((x) => (
              <button key={x.id} onClick={() => onOuvrir(x.id)} className="w-full text-left px-4 py-3 text-[15px] active:bg-[#F1F5F9]">
                {x.prenom} {x.nom}
              </button>
            ))}
          </Groupe>
        </section>
      )}
    </div>
  );
};

export const Eleves = ({ cible }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [fiche, setFiche] = useState(null);
  // Arrivée depuis le tableau de bord ou une alerte : la fiche, ou la recherche, est prête.
  useEffect(() => {
    if (cible?.racine) setFiche(null);
    if (cible?.fiche) setFiche(cible.fiche);
    if (cible?.q) { setFiche(null); setQ(cible.q); }
  }, [cible]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return undefined; }
    const t = setTimeout(() => {
      api(`/eleves?q=${encodeURIComponent(q.trim())}&limite=40`).then(setRes).catch(() => setRes({ resultats: [] }));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  if (fiche) return <Fiche id={fiche} onRetour={() => setFiche(null)} onOuvrir={setFiche} />;
  return (
    <div className="space-y-4" data-testid="ecole-eleves">
      <label className="flex items-center gap-2 rounded-2xl px-3 min-h-[46px] bg-white" style={{ border: "1px solid var(--border)" }}>
        <Search size={18} className="text-[var(--muted)]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Élève, parent ou e-mail"
          className="flex-1 outline-none text-[16px] bg-transparent" data-testid="ecole-eleves-recherche" />
      </label>
      {!res && <p className="text-[13px] text-[var(--muted)] px-1">Tape au moins deux lettres : prénom, nom de l'élève ou du parent, adresse e-mail.</p>}
      {res && !res.resultats.length && <Vide>Personne trouvé.</Vide>}
      {res && res.resultats.length > 0 && (
        <Groupe>
          {res.resultats.map((e) => (
            <button key={e.id} onClick={() => setFiche(e.id)} data-testid={`ecole-eleve-${e.id}`}
              className="w-full text-left px-4 py-3 active:bg-[#F1F5F9]">
              <div className="text-[15px] font-medium">{e.prenom} {e.nom}</div>
              <div className="text-[12.5px] text-[var(--muted)] truncate">
                {[e.cours?.join(", ") || "aucun cours actif", e.responsable].filter(Boolean).join(" · ")}
              </div>
            </button>
          ))}
        </Groupe>
      )}
    </div>
  );
};
